/**
 * Cmd+K / Ctrl+K — búsqueda global en el portal.
 *
 * v8.5.49 · piel · resultados agrupados por PIEZAS · PEDIDOS · PANTALLAS ·
 * DOCUMENTOS. El backend (GET /cliente/:id/search) solo busca en 4 tipos —
 * tarea/documento/reporte/produccion, sin "pedido" ni "pantalla" — y esta
 * pantalla no toca el backend, así que:
 *   - tarea    → Piezas
 *   - documento→ Documentos
 *   - reporte, produccion → Pantallas (son saltos a una pantalla puntual:
 *     el reporte del mes, el calendario) — no ameritan grupo propio.
 *   - Pedidos: no hay endpoint de búsqueda de pedidos, así que se reusa
 *     api.pedidos() (ya existe, Pedidos.jsx lo llama igual) y se filtra
 *     por título/descripción en el cliente, mismo criterio que ya aplica
 *     el backend para tareas/documentos.
 *   - Pantallas: lista estática de rutas (nunca pega al backend) para
 *     saltar directo a una sección por nombre.
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Loader2, FileText, BarChart3, ListChecks, Film, Inbox, LayoutGrid, CornerDownLeft } from "lucide-react";
import { api } from "../lib/api.js";
import { limpiarTitulo } from "../lib/parseNombreTarea.js";
import { useAuth } from "../context/AuthContext.jsx";
import EstadoBadge from "./EstadoBadge.jsx";

const TIPO_ICON = {
  tarea: ListChecks,
  documento: FileText,
  reporte: BarChart3,
  produccion: Film,
  pedido: Inbox,
  pantalla: LayoutGrid,
};

const GRUPO_DE_TIPO = {
  tarea: "Piezas",
  pedido: "Pedidos",
  reporte: "Pantallas",
  produccion: "Pantallas",
  pantalla: "Pantallas",
  documento: "Documentos",
};
const ORDEN_GRUPOS = ["Piezas", "Pedidos", "Pantallas", "Documentos"];

const PANTALLAS_ESTATICAS = [
  { titulo: "Hoy",            url: "/" },
  { titulo: "Trabajo",        url: "/tareas" },
  { titulo: "Guardadas",      url: "/tareas/favoritos" },
  { titulo: "Campañas",       url: "/tareas/campanas" },
  { titulo: "Plan del mes",   url: "/planes" },
  { titulo: "Calendario",     url: "/planes/calendario" },
  { titulo: "Feed",           url: "/planes/feed" },
  { titulo: "Coberturas",     url: "/planes/coberturas" },
  { titulo: "Resultados",     url: "/reportes" },
  { titulo: "Galería",        url: "/reportes/galeria" },
  { titulo: "Cuenta",         url: "/cuenta/brand" },
  { titulo: "Documentos",     url: "/cuenta/documentos" },
  { titulo: "Tu equipo",      url: "/cuenta/equipo" },
  { titulo: "Facturas",       url: "/cuenta/facturas" },
  { titulo: "Configuración",  url: "/cuenta/configuracion" },
  { titulo: "Chat",           url: "/chat" },
];

export default function CommandPalette() {
  const { clienteActivoId } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [backendResults, setBackendResults] = useState([]);
  const [pedidosAll, setPedidosAll] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const inputRef = useRef(null);

  // Atajo Cmd+K / Ctrl+K
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen(true);
      } else if (e.key === "Escape" && open) {
        setOpen(false);
      }
    };
    // La barra de búsqueda del encabezado abre la paleta con un evento propio (antes simulaba un ⌘K sintético).
    const abrir = () => setOpen(true);
    window.addEventListener("keydown", handler);
    window.addEventListener("portal:abrir-buscador", abrir);
    return () => { window.removeEventListener("keydown", handler); window.removeEventListener("portal:abrir-buscador", abrir); };
  }, [open]);

  // Focus al abrir + traer pedidos una vez (se filtran en el cliente, ver nota arriba)
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
      setQ("");
      setBackendResults([]);
      setSelectedIdx(0);
      if (clienteActivoId) api.pedidos(clienteActivoId).then(r => setPedidosAll(r.pedidos || [])).catch(() => setPedidosAll([]));
    }
  }, [open, clienteActivoId]);

  // Buscar debounced (backend: tareas/documentos/reportes/producciones)
  useEffect(() => {
    if (!q.trim() || q.length < 2 || !clienteActivoId) {
      setBackendResults([]);
      return;
    }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.searchGlobal(clienteActivoId, q);
        setBackendResults(r.results || []);
        setSelectedIdx(0);
      } finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [q, clienteActivoId]);

  // Combina backend + pedidos (filtrados en cliente) + pantallas estáticas
  const qLower = q.trim().toLowerCase();
  const pedidoResults = qLower.length >= 2
    ? pedidosAll
        .filter(p => (p.titulo || "").toLowerCase().includes(qLower) || (p.descripcion || "").toLowerCase().includes(qLower))
        .slice(0, 5)
        .map(p => ({ tipo: "pedido", id: p.id, titulo: p.titulo, subtitulo: p.estado || "Pedido", url: "/tareas/pedidos" }))
    : [];
  const pantallaResults = qLower.length >= 2
    ? PANTALLAS_ESTATICAS.filter(p => p.titulo.toLowerCase().includes(qLower)).slice(0, 5)
        .map(p => ({ tipo: "pantalla", id: p.url, titulo: p.titulo, subtitulo: "Pantalla", url: p.url }))
    : [];
  const results = [...backendResults, ...pedidoResults, ...pantallaResults];

  const grupos = ORDEN_GRUPOS
    .map(g => ({ label: g, items: results.filter(r => GRUPO_DE_TIPO[r.tipo] === g) }))
    .filter(g => g.items.length > 0);

  // Navegación teclado (sobre la lista aplanada, en el orden en que se ve)
  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIdx(i => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIdx(i => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && results[selectedIdx]) {
      e.preventDefault();
      ir(results[selectedIdx]);
    }
  };

  // v8.5.61 · piel · el backend manda url:"/documentos" a secas (sin id) para
  // resultados de documento — no se puede tocar esa ruta del backend solo
  // para esto, así que acá se arma el deep link real con el id que el
  // resultado ya trae, resaltable por Documentos.jsx vía `?doc=`.
  const ir = (r) => {
    setOpen(false);
    navigate(r.tipo === "documento" ? `/cuenta/documentos?doc=${r.id}` : r.url);
  };

  if (!open) return null;

  // v8.5.49 · piel · label de EstadoBadge para resultados de tarea: el
  // backend manda subtitulo="{tipo_contenido} · {estado_label}" (no manda
  // el estado crudo) — se extrae la parte después de "·" en vez de tocar
  // el endpoint solo para esto.
  const estadoLabelDeTarea = (r) => {
    if (r.tipo !== "tarea" || !r.subtitulo?.includes("·")) return null;
    return r.subtitulo.split("·").pop().trim() || null;
  };

  let idxGlobal = -1;

  return (
    // v8.5.69 · fix auditoría móvil · pt-8 en mobile (antes pt-20 fijo): en
    // un teléfono chico con el teclado abierto, ese padding + el alto de
    // resultados dejaba la parte de abajo de la lista tapada por el
    // teclado sin ningún scroll que la revele (ver también max-h abajo).
    <div className="fixed inset-0 z-[var(--z-modal)] bg-black/50 backdrop-blur-sm flex items-start justify-center pt-8 sm:pt-20 px-4" onClick={() => setOpen(false)}>
      <div role="dialog" aria-modal="true" aria-label="Buscar" className="w-full max-w-[620px] bg-[var(--raised)] rounded-[20px] shadow-overlay overflow-hidden"
           onClick={e => e.stopPropagation()}>
        {/* Search input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-[var(--line)]">
          {loading ? <Loader2 className="h-4 w-4 text-[var(--faint)] animate-spin"/> : <Search className="h-4 w-4 text-[var(--faint)]"/>}
          <input
            ref={inputRef}
            autoFocus
            value={q}
            onChange={e => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Buscar piezas, pedidos, pantallas, documentos…"
            className="flex-1 bg-transparent text-[18px] focus:outline-none text-[var(--ink)] placeholder:text-[var(--faint)]"
          />
        </div>

        {/* Results */}
        <div className="max-h-[50dvh] sm:max-h-[400px] overflow-y-auto">
          {q.length < 2 ? (
            <div className="p-8 text-center">
              <p className="text-[15px] text-[var(--muted)]">Escribí al menos 2 letras para buscar en todo el portal</p>
              <div className="mt-4 flex justify-center gap-1.5 flex-wrap">
                {["Reel", "Reporte", "Plan", "Cobertura"].map(s => (
                  <button key={s} onClick={() => setQ(s)}
                          className="text-[13px] px-2 py-1 bg-[var(--surface)] hover:bg-[var(--panel)] rounded-full text-[var(--ink-3)]">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : results.length === 0 && !loading ? (
            <div className="p-8 text-center">
              <p className="text-[15px] text-[var(--ink-2)]">Nada con ese nombre "<b>{q}</b>"</p>
              <div className="mt-4 flex justify-center gap-1.5 flex-wrap">
                {PANTALLAS_ESTATICAS.slice(0, 3).map(p => (
                  <button key={p.url} onClick={() => { setOpen(false); navigate(p.url); }}
                          className="text-[13px] px-2 py-1 bg-[var(--surface)] hover:bg-[var(--panel)] rounded-full text-[var(--ink-3)]">
                    {p.titulo}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            grupos.map(g => (
              <div key={g.label}>
                <div className="font-mono text-[12px] font-medium uppercase tracking-[.08em] text-[var(--faint)] px-4 pt-3 pb-1">
                  {g.label}
                </div>
                {g.items.map(r => {
                  idxGlobal += 1;
                  const i = idxGlobal;
                  const Icon = TIPO_ICON[r.tipo] || FileText;
                  const estadoLabel = estadoLabelDeTarea(r);
                  return (
                    <button key={`${r.tipo}-${r.id}`}
                            onClick={() => ir(r)}
                            onMouseEnter={() => setSelectedIdx(i)}
                            className={`w-full px-4 py-2.5 flex items-center gap-3 text-left transition border-l-2 ${
                              selectedIdx === i ? "bg-[var(--surface)] border-[var(--ink)]" : "border-transparent hover:bg-[var(--panel)]/60"
                            }`}>
                      <Icon className="h-4 w-4 text-[var(--faint)] flex-shrink-0"/>
                      <div className="flex-1 min-w-0">
                        <div className="text-[15px] text-[var(--ink)] truncate">{limpiarTitulo(r.titulo)}</div>
                        {!estadoLabel && (
                          <div className="text-[13px] text-[var(--muted)] truncate">
                            {r.subtitulo}
                            {r.fecha && <span> · {new Date(r.fecha + "T12:00").toLocaleDateString("es-PY", { day: "2-digit", month: "short" })}</span>}
                          </div>
                        )}
                      </div>
                      {estadoLabel && <EstadoBadge label={estadoLabel} icon={r.icono}/>}
                      {selectedIdx === i && <CornerDownLeft className="h-3.5 w-3.5 text-[var(--faint)] flex-shrink-0"/>}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        {results.length > 0 && (
          <div className="px-4 py-2 bg-[var(--surface)] border-t border-[var(--line)] flex items-center justify-between font-mono text-[12px] text-[var(--faint)]">
            <span>{results.length} resultado{results.length === 1 ? "" : "s"}</span>
            <div className="flex items-center gap-3">
              <span><kbd className="px-1 py-0.5 bg-[var(--panel)] rounded-full">↑↓</kbd> navegar</span>
              <span><kbd className="px-1 py-0.5 bg-[var(--panel)] rounded-full">↵</kbd> abrir</span>
              <span><kbd className="px-1 py-0.5 bg-[var(--panel)] rounded-full">esc</kbd> cerrar</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
